/**
 * cl-cards/pure — 데이터 카드 배치 생성기의 순수부 (지시서 §1-4.3 · 레인 A).
 *
 * 1차 범위: ① 단계 카드(현장별) ② 시세 카드(동 단위 84㎡대 12개월) ③ 지역 파이프라인 카드(시군구 단위).
 * - 배치로 PNG 를 미리 만들어 저장소(gen-images 버킷 `sites/cl/…`)에 올리고 `/img/cl/…` 로 서빙한다.
 *   (`/img/:path*` → gen-images/sites/:path* 기존 리라이트 그대로 — 새 매핑 0)
 * - 키에 데이터 해시를 넣는다. 데이터가 바뀌면 새 키 → URL 이 바뀌어 immutable 캐시가 안전하다.
 * - 요청마다 satori 를 돌리지 않는다. 페이지는 현장별 매니페스트(JSON)만 읽는다.
 * - 이모지 금지. 「조감도」 금지(카드는 공식 이미지가 아니다).
 * ⛔ DB·네트워크·fs·env 0.
 */
import { eokShort } from '@/lib/fmt';
import { stepperKindOf, stepperPosition, type StepperKind } from '@/lib/cl-core';

export const CL_CARD_BUCKET = 'gen-images';
/** 버킷 안 접두. 공개 경로는 `/img/cl/…`. */
export const CL_CARD_PREFIX = 'sites/cl';
export const CL_CARD_W = 1200;
export const CL_CARD_H = 675;
/** 렌더 규격이 바뀌면 올린다(해시에 들어가 전량 재생성). */
export const CL_CARD_VERSION = 1;

export type ClCardKind = 'stage' | 'price' | 'region';

/** FNV-1a 32bit → 8자리 hex. */
export function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

function stable(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
}

export function dataHash(kind: ClCardKind, data: unknown): string {
  return fnv1a(`${CL_CARD_VERSION}|${kind}|${stable(data)}`) + fnv1a(`${stable(data)}|${kind}`).slice(0, 4);
}

/* ───────── ① 단계 카드 ───────── */

export interface StageCardData {
  name: string;
  place: string;
  kind: StepperKind;
  stage: string;
  current: number;
  steps: string[];
  currentLabel: string;
}

export function stageCardData(input: {
  name: string;
  sigungu?: string | null;
  dong?: string | null;
  region?: string | null;
  siteType?: string | null;
  hasRedevSource?: boolean;
  lifecycleStage?: string | null;
}): StageCardData | null {
  const kind = stepperKindOf(input.siteType, input.hasRedevSource);
  const pos = stepperPosition(input.lifecycleStage, kind);
  if (pos.current == null || !String(input.name || '').trim()) return null;
  return {
    name: String(input.name).trim(),
    place: [input.region, input.sigungu, input.dong].map((s) => String(s ?? '').trim()).filter(Boolean).join(' '),
    kind,
    stage: String(input.lifecycleStage),
    current: pos.current,
    steps: pos.steps.map((s) => s.label),
    currentLabel: pos.steps[pos.current].label,
  };
}

/* ───────── ② 시세 카드 ───────── */

export interface PriceCardData {
  /** 「부산 해운대구 우동」 */
  place: string;
  /** 「84㎡대」 */
  band: string;
  months: number;
  count: number;
  /** 만원 */
  median: number;
  min: number;
  max: number;
}

export function median(nums: number[]): number | null {
  const a = nums.filter((n) => Number.isFinite(n)).slice().sort((x, y) => x - y);
  if (a.length === 0) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : Math.round((a[m - 1] + a[m]) / 2);
}

/** 84㎡대 = 전용 75~95㎡. 3건 미만이면 카드를 만들지 않는다(대표성 없음). */
export const PRICE_BAND = { min: 75, max: 95, label: '84㎡대' } as const;
export const PRICE_MIN_COUNT = 3;

export function priceCardData(place: string, amounts: number[], months = 12): PriceCardData | null {
  const a = amounts.filter((n) => Number.isFinite(n) && n > 0);
  if (a.length < PRICE_MIN_COUNT || !place.trim()) return null;
  return { place: place.trim(), band: PRICE_BAND.label, months, count: a.length, median: median(a)!, min: Math.min(...a), max: Math.max(...a) };
}

/** 만원 → 「5.2억」/「8,500만」 */
export function fmtManwon(n: number): string {
  return eokShort(n) ?? ''; // 정본 lib/fmt(버림 · 「.0」 제거)
}

/**
 * 거래 건수 표기 — 표본 조회가 상한에 닿았으면(capped) 「N건 이상」.
 * site-audit(2026-10-25): limit(1000) 표본 길이를 그대로 「1,000건」으로 내서 큰 시군구(창원시 등)가 정확히 1,000건인 것처럼 보였다.
 */
export function sampleCountText(count: number, capped?: boolean): string {
  const n = Math.max(0, Math.round(Number(count) || 0)).toLocaleString('ko-KR');
  return capped ? `${n}건 이상` : `${n}건`;
}

/* ───────── ③ 지역 파이프라인 카드 ───────── */

export const PIPELINE_GROUPS: Array<{ key: string; label: string; stages: string[] }> = [
  { key: 'redev', label: '정비사업 진행', stages: ['union_established', 'constructor_selected', 'plan_approved', 'mgmt_approved'] },
  { key: 'upcoming', label: '분양 예정', stages: ['site_planning', 'pre_announcement'] },
  { key: 'open', label: '청약·잔여 분양', stages: ['subscription_open', 'award_pending', 'award_announced', 'unsold_active', 'special_supply'] },
  { key: 'build', label: '공사·입주 예정', stages: ['construction', 'pre_move_in', 'move_in_ready', 'move_in', 'move_in_started'] },
];

export interface RegionCardData {
  place: string;
  groups: Array<{ label: string; count: number }>;
  total: number;
}

export function regionCardData(place: string, stages: Array<string | null | undefined>): RegionCardData | null {
  const groups = PIPELINE_GROUPS.map((g) => ({ label: g.label, count: stages.filter((s) => !!s && g.stages.indexOf(s) >= 0).length }));
  const total = groups.reduce((a, g) => a + g.count, 0);
  if (total === 0 || !place.trim()) return null;
  return { place: place.trim(), groups, total };
}

/* ───────── 키·경로·매니페스트 ───────── */

/** 저장 키(버킷 안 경로). 현장 카드는 site uuid, 공유 카드는 해시만(한글 0 — URL 인코딩 함정 회피). */
export function cardStorageKey(kind: ClCardKind, data: unknown, siteId?: string | null): string {
  const h = dataHash(kind, data);
  return kind === 'stage' && siteId ? `${CL_CARD_PREFIX}/${kind}/${siteId}-${h}.png` : `${CL_CARD_PREFIX}/${kind}/${h}.png`;
}

/** 저장 키 → 공개 경로 `/img/cl/…`. */
export function cardPublicPath(storageKey: string): string {
  return `/img/${storageKey.replace(/^sites\//, '')}`;
}

export interface ClCardManifest {
  v: number;
  site_id: string;
  built_at: string;
  stage?: { path: string; label: string } | null;
  price?: { path: string; place: string } | null;
  region?: { path: string; place: string } | null;
}

export function manifestStorageKey(siteId: string): string {
  return `${CL_CARD_PREFIX}/m/${siteId}.json`;
}

/** 매니페스트 검증(형식이 틀리면 null — 페이지는 카드 없이 간다). */
export function parseManifest(v: unknown): ClCardManifest | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (typeof o.site_id !== 'string') return null;
  const ok = (x: unknown) => !!x && typeof x === 'object' && typeof (x as { path?: unknown }).path === 'string' && /^\/img\/cl\/[a-z]+\/[0-9a-f-]+\.png$/.test((x as { path: string }).path);
  return {
    v: Number(o.v) || 0,
    site_id: o.site_id,
    built_at: String(o.built_at ?? ''),
    stage: ok(o.stage) ? (o.stage as ClCardManifest['stage']) : null,
    price: ok(o.price) ? (o.price as ClCardManifest['price']) : null,
    region: ok(o.region) ? (o.region as ClCardManifest['region']) : null,
  };
}
